import { LocationData, SalesTier } from '../services/DashboardService';
import { AnomalyFlag } from './anomalyDetection';
import { BaselineData } from '../services/baselineService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatCurrency, formatPercent, formatPercentWithSign, formatDisplayDate } from './formatters';
import { normalizeDate } from './dateUtils';

export interface SalesTierGroup {
  tierName: string;
  tierRange: string;
  locations: LocationData[];
}

/**
 * Determines sales tier based on weekly sales
 */
function getSalesTier(weeklyTotal: number): SalesTierGroup {
  if (weeklyTotal > 35000) {
    return { tierName: 'High Volume', tierRange: '>$35k/week', locations: [] };
  } else if (weeklyTotal >= 25000) {
    return { tierName: 'Upper Mid', tierRange: '$25k-$35k/week', locations: [] };
  } else if (weeklyTotal >= 20000) {
    return { tierName: 'Lower Mid', tierRange: '$20k-$25k/week', locations: [] };
  } else {
    return { tierName: 'Base', tierRange: '<$20k/week', locations: [] };
  }
}

/**
 * Groups locations by sales tier
 */
function groupBySalesTier(locations: LocationData[]): SalesTierGroup[] {
  const tierMap = new Map<string, SalesTierGroup>();
  
  locations.forEach(location => {
    const tier = getSalesTier(location.weeklyTotal);
    const key = tier.tierName;
    
    if (!tierMap.has(key)) {
      tierMap.set(key, { ...tier, locations: [] });
    }
    
    tierMap.get(key)!.locations.push(location);
  });
  
  // Return in order: High Volume, Upper Mid, Lower Mid, Base
  const order = ['High Volume', 'Upper Mid', 'Lower Mid', 'Base'];
  return order
    .filter(tierName => tierMap.has(tierName))
    .map(tierName => tierMap.get(tierName)!);
}

/**
 * Calculates global average for a sales tier across all locations
 */
function calculateGlobalAverage(locations: LocationData[]): BaselineData {
  if (locations.length === 0) {
    return {
      weeklyToast: 0,
      weeklyCatering: 0,
      weeklyTotal: 0,
      weeklyGfs: 0,
      gfsPercent: 0,
      varianceFromAvg: 0,
      eightWeekTotalGfs: 0,
      eightWeekTotalSales: 0,
    };
  }
  
  const weeklyToast = locations.reduce((sum, loc) => sum + loc.weeklySalesToast, 0) / locations.length;
  const weeklyCatering = locations.reduce((sum, loc) => sum + (loc.weeklyCatering ?? 0), 0) / locations.length;
  const weeklyTotal = locations.reduce((sum, loc) => sum + loc.weeklyTotal, 0) / locations.length;
  const weeklyGfs = locations.reduce((sum, loc) => sum + (loc.weeklyGfs ?? 0), 0) / locations.length;
  const gfsPercent = locations.reduce((sum, loc) => sum + loc.gfsPercent, 0) / locations.length;
  
  return {
    weeklyToast,
    weeklyCatering,
    weeklyTotal,
    weeklyGfs,
    gfsPercent,
    varianceFromAvg: 0,
    eightWeekTotalGfs: locations.reduce((sum, loc) => sum + (loc.eightWeekTotalGfs ?? 0), 0) / locations.length,
    eightWeekTotalSales: locations.reduce((sum, loc) => sum + loc.eightWeekTotalSales, 0) / locations.length,
  };
}

/**
 * Gets baseline average for a sales tier from baseline data map
 */
function getBaselineForTier(
  tierName: string,
  baselineDataByTier: Record<string, BaselineData>
): BaselineData | null {
  return baselineDataByTier[tierName] || null;
}

/**
 * Formats a date range for filename, handling cross-year scenarios
 */
function formatDateRangeForFilename(startDate: string, endDate: string): string {
  // Parse YYYY-MM-DD format without timezone conversion
  const parseDate = (dateStr: string): Date => {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return new Date(dateStr);
  };
  
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  
  const formatDate = (date: Date, includeYear: boolean) => {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    // Remove comma before year: "Nov_23_2025" instead of "Nov_23,_2025"
    return includeYear ? `${month}_${day}_${year}` : `${month}_${day}`;
  };
  
  // If years differ, include year in both dates
  if (startYear !== endYear) {
    return `${formatDate(start, true)}_-_${formatDate(end, true)}`;
  }
  
  // Same year: only include year in end date
  return `${formatDate(start, false)}_-_${formatDate(end, true)}`;
}

/**
 * Exports DM report grouped by sales tier
 */
export function exportDMReport(
  dmName: string,
  dmLocations: LocationData[],
  allTiers: SalesTier[],
  anomalyFlags: Map<string, AnomalyFlag>,
  baselineDataByTier: Record<string, BaselineData>,
  dateRangeLabel: string,
  startDate?: string,
  endDate?: string,
  filename?: string
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt' });
  
  // Calculate prior week date range for display (last week of the 8-week range)
  const priorWeekDateRangeText = startDate && endDate
    ? (() => {
        // The "prior week" is the last week (8th week) of the 8-week range
        // The backend uses ISO week boundaries (Monday-Sunday)
        // We need to find the ISO week containing endDate
        const endDateObj = normalizeDate(endDate);
        const startDateObj = normalizeDate(startDate);
        
        // Get ISO week boundaries for the week containing endDate
        // ISO weeks start on Monday (day 1) and end on Sunday (day 0)
        const dayOfWeek = endDateObj.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        
        // Calculate days to subtract to get to Monday of the ISO week
        // If Sunday (0), go back 6 days to get Monday; otherwise go back (dayOfWeek - 1) days
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        
        const isoWeekMonday = new Date(endDateObj);
        isoWeekMonday.setDate(isoWeekMonday.getDate() - daysToMonday);
        
        // The week start is the ISO week Monday, but don't go before the range start
        const weekStart = isoWeekMonday >= startDateObj ? isoWeekMonday : startDateObj;
        
        // The week end is the endDate (Sunday of the ISO week, capped at range end)
        const weekEnd = endDateObj;
        
        const formatDate = (date: Date): string => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        return `${formatDisplayDate(formatDate(weekStart))}-${formatDisplayDate(formatDate(weekEnd))}`;
      })()
    : '';
  
  // Header
  doc.setFontSize(16);
  doc.text(`DM Report: ${dmName}`, 40, 40);
  doc.setFontSize(12);
  doc.text(`Period: ${dateRangeLabel}`, 40, 60);
  
  let startY = 80;
  
  // Group locations by sales tier
  const salesTierGroups = groupBySalesTier(dmLocations);
  
  // Calculate global averages for each sales tier across all locations in the system
  // Map by tier name and also by tier range for flexible matching
  const globalAveragesByTier = new Map<string, BaselineData>();
  const tierRangeMap = new Map<string, string>(); // Maps tier range to tier name
  const tierNameMap = new Map<string, string>(); // Maps short tier name to full tier name
  
  allTiers.forEach(tier => {
    globalAveragesByTier.set(tier.tierName, calculateGlobalAverage(tier.locations));
    if (tier.tierRange) {
      tierRangeMap.set(tier.tierRange, tier.tierName);
    }
    // Extract short tier name from full tier name (e.g., "High Volume (>$35k/week)" -> "High Volume")
    const shortName = tier.tierName.split(' (')[0];
    tierNameMap.set(shortName, tier.tierName);
  });
  
  salesTierGroups.forEach((tierGroup, tierIdx) => {
    if (tierGroup.locations.length === 0) {
      return;
    }
    
    // Check if we need a new page
    if (startY > doc.internal.pageSize.getHeight() - 150) {
      doc.addPage();
      startY = 40;
    }
    
    // Sales Tier Header
    doc.setFontSize(13);
    doc.text(`Sales Tier: ${tierGroup.tierName} (${tierGroup.tierRange})`, 40, startY);
    startY += 20;
    
    // Get global average and baseline for this tier
    // Backend uses format like "High Volume (>$35k/week)" but frontend uses "High Volume"
    // Try to match by short name first, then by tier range
    let matchedTierName = tierGroup.tierName;
    
    // First try to find full tier name from short name
    if (tierNameMap.has(tierGroup.tierName)) {
      matchedTierName = tierNameMap.get(tierGroup.tierName)!;
    } else if (tierRangeMap.has(tierGroup.tierRange)) {
      // Fallback to matching by range
      matchedTierName = tierRangeMap.get(tierGroup.tierRange)!;
    } else if (globalAveragesByTier.has(tierGroup.tierName)) {
      // Direct match
      matchedTierName = tierGroup.tierName;
    }
    
    const globalAvg = globalAveragesByTier.get(matchedTierName);
    const baseline = getBaselineForTier(matchedTierName, baselineDataByTier);
    
    // Helper function to get 8-week GFS percent
    const getEightWeekPercent = (location: LocationData): number | null => {
      if (location.eightWeekGfsPercentValue !== undefined && location.eightWeekGfsPercentValue !== null) {
        return location.eightWeekGfsPercentValue;
      }
      if (
        !location.eightWeekTotalSales ||
        location.eightWeekTotalSales <= 0 ||
        location.eightWeekTotalGfs === null ||
        location.eightWeekTotalGfs === undefined
      ) {
        return null;
      }
      return (location.eightWeekTotalGfs / location.eightWeekTotalSales) * 100;
    };

    // Calculate average 8-week GFS percent for this tier to calculate variance
    const tierEightWeekPercents = tierGroup.locations
      .map(loc => getEightWeekPercent(loc))
      .filter((p): p is number => p !== null);
    const tierAvgEightWeekPercent = tierEightWeekPercents.length > 0
      ? tierEightWeekPercents.reduce((sum, p) => sum + p, 0) / tierEightWeekPercents.length
      : null;

    // Table headers - matching dashboard order: Prior Week first, then 8-Wk Avg
    // Include date range in parentheses for Prior Week Total Sales and Prior Week GFS Spend
    const headers = [
      'Location',
      priorWeekDateRangeText ? `Prior Week Total Sales (${priorWeekDateRangeText})` : 'Prior Week Total Sales',
      priorWeekDateRangeText ? `Prior Week GFS Spend (${priorWeekDateRangeText})` : 'Prior Week GFS Spend',
      'Prior Week GFS %',
      'Prior Week GFS Var from Avg',
      '8-Wk Avg Toast',
      '8-Wk Avg Cater',
      '8-Wk Avg Weekly Sales',
      '8-Wk Avg GFS Spend',
      '8-Wk GFS %',
      '8-Wk GFS Var from Avg',
    ];
    
    // Build table body with row styles
    const body: any[] = [];
    const rowStyles: any[] = [];
    
    tierGroup.locations.forEach(location => {
      const hasAnomaly = anomalyFlags.has(location.location);
      const priorWeekGfsPercent = getEightWeekPercent(location);
      
      // Calculate variance if not present
      let priorWeekVariance: number | null = location.eightWeekGfsVariance ?? null;
      if (priorWeekVariance === null && priorWeekGfsPercent !== null && tierAvgEightWeekPercent !== null) {
        priorWeekVariance = priorWeekGfsPercent - tierAvgEightWeekPercent;
      }
      
      body.push([
        location.location,
        // Prior Week columns
        location.eightWeekTotalSales > 0 ? formatCurrency(location.eightWeekTotalSales) : '--',
        location.eightWeekTotalGfs !== null && location.eightWeekTotalGfs > 0 ? formatCurrency(location.eightWeekTotalGfs) : '--',
        priorWeekGfsPercent !== null ? formatPercent(priorWeekGfsPercent) : '--',
        priorWeekVariance !== null ? formatPercentWithSign(priorWeekVariance) : '--',
        // 8-Wk Avg columns
        formatCurrency(location.weeklySalesToast),
        location.weeklyCatering !== null ? formatCurrency(location.weeklyCatering) : '--',
        formatCurrency(location.weeklyTotal),
        location.weeklyGfs !== null && location.weeklyGfs > 0 ? formatCurrency(location.weeklyGfs) : '--',
        formatPercent(location.gfsPercent),
        formatPercentWithSign(location.varianceFromAvg ?? 0),
      ]);
      
      // Add row style for locations with anomalies (slightly red background)
      if (hasAnomaly) {
        rowStyles.push({
          fillColor: [255, 245, 245], // Very light red
        });
      } else {
        rowStyles.push({});
      }
    });
    
    // Add sales tier average row (always add if we have locations in this tier)
    if (globalAvg) {
      // Calculate prior week averages for the tier
      const priorWeekTotalSalesAvg = tierGroup.locations.reduce((sum, loc) => sum + (loc.eightWeekTotalSales ?? 0), 0) / tierGroup.locations.length;
      const priorWeekGfsAvg = tierGroup.locations.reduce((sum, loc) => sum + (loc.eightWeekTotalGfs ?? 0), 0) / tierGroup.locations.length;
      const priorWeekGfsPercentAvg = priorWeekTotalSalesAvg > 0 ? (priorWeekGfsAvg / priorWeekTotalSalesAvg) * 100 : null;
      
      body.push([
        'Sales Tier Avg',
        // Prior Week columns
        priorWeekTotalSalesAvg > 0 ? formatCurrency(priorWeekTotalSalesAvg) : '--',
        priorWeekGfsAvg > 0 ? formatCurrency(priorWeekGfsAvg) : '--',
        priorWeekGfsPercentAvg !== null ? formatPercent(priorWeekGfsPercentAvg) : '--',
        formatPercent(0), // 0.0% for variance
        // 8-Wk Avg columns
        formatCurrency(globalAvg.weeklyToast),
        formatCurrency(globalAvg.weeklyCatering),
        formatCurrency(globalAvg.weeklyTotal),
        globalAvg.weeklyGfs > 0 ? formatCurrency(globalAvg.weeklyGfs) : '--',
        formatPercent(globalAvg.gfsPercent),
        formatPercent(0), // 0.0% for variance
      ]);
      rowStyles.push({
        fontStyle: 'bold',
        fillColor: [245, 245, 245], // Light gray for average rows
      });
    } else {
      // Add placeholder if global avg not found (for debugging)
      console.warn(`Global average not found for tier: ${tierGroup.tierName} (${tierGroup.tierRange}), matched: ${matchedTierName}`);
    }
    
    // Add baseline average row
    if (baseline) {
      body.push([
        'Baseline Avg',
        // Prior Week columns - use baseline's 8-week data
        baseline.eightWeekTotalSales > 0 ? formatCurrency(baseline.eightWeekTotalSales) : '--',
        baseline.eightWeekTotalGfs > 0 ? formatCurrency(baseline.eightWeekTotalGfs) : '--',
        baseline?.eightWeekGfsPercent !== undefined && baseline?.eightWeekGfsPercent !== null
          ? formatPercent(baseline.eightWeekGfsPercent)
          : baseline.eightWeekTotalSales > 0 && baseline.eightWeekTotalGfs > 0
          ? formatPercent((baseline.eightWeekTotalGfs / baseline.eightWeekTotalSales) * 100)
          : '--',
        formatPercent(0), // 0.0% for variance
        // 8-Wk Avg columns
        formatCurrency(baseline.weeklyToast),
        formatCurrency(baseline.weeklyCatering),
        formatCurrency(baseline.weeklyTotal),
        baseline.weeklyGfs > 0 ? formatCurrency(baseline.weeklyGfs) : '--',
        formatPercent(baseline.gfsPercent),
        formatPercent(0), // 0.0% for variance
      ]);
      rowStyles.push({
        fontStyle: 'bold',
        fillColor: [245, 245, 245], // Light gray for average rows
      });
    } else {
      // Add placeholder if baseline not found (for debugging)
      console.warn(`Baseline not found for tier: ${tierGroup.tierName} (${tierGroup.tierRange}), matched: ${matchedTierName}`);
    }
    
    // Create table
    autoTable(doc, {
      head: [headers],
      body,
      startY,
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [122, 31, 42],
        textColor: 255,
        fontSize: 9,
      },
      bodyStyles: {
        cellPadding: 4,
      },
      alternateRowStyles: {
        fillColor: false, // Disable default alternating colors
      },
      didParseCell: (data: any) => {
        // Style header cells with parentheses (date range) - make them smaller
        if (data.section === 'head') {
          const cellText = data.cell.text?.[0] || '';
          // Check if this is Prior Week Total Sales or Prior Week GFS Spend column (columns 1 and 2)
          if (data.column.index === 1 || data.column.index === 2) {
            if (cellText.includes('(')) {
              // Apply smaller font size for headers with parentheses
              data.cell.styles.fontSize = 7;
            }
          }
          return;
        }
        
        // Apply row styles to body rows only
        // data.row.index is 0-based for body rows (0 = first body row, not header)
        const bodyRowIndex = data.row.index;
        if (bodyRowIndex >= 0 && bodyRowIndex < rowStyles.length && rowStyles[bodyRowIndex]) {
          Object.assign(data.cell.styles, rowStyles[bodyRowIndex]);
        }
        
      },
      margin: { left: 40, right: 40 },
      theme: 'grid',
    });
    
    startY = (doc as any).lastAutoTable.finalY + 20;
    
    // Add spacing between tiers
    if (tierIdx < salesTierGroups.length - 1) {
      startY += 10;
    }
  });
  
  // Save the PDF
  let finalFilename: string;
  if (filename) {
    finalFilename = filename;
  } else if (startDate && endDate) {
    // Use properly formatted date range that handles cross-year scenarios
    const dateRangeStr = formatDateRangeForFilename(startDate, endDate);
    finalFilename = `dm_report_${dmName.replace(/\s+/g, '_')}_${dateRangeStr}.pdf`;
  } else {
    // Fallback to using dateRangeLabel (may not handle cross-year properly)
    finalFilename = `dm_report_${dmName.replace(/\s+/g, '_')}_${dateRangeLabel.replace(/\s+/g, '_')}.pdf`;
  }
  doc.save(finalFilename);
}

